import React, { useEffect, useMemo, useRef } from 'react'
import { LoaderFunctionArgs, useLoaderData, useRouteError, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import i18n from 'i18next'
import { CustomReport, PrintStyleSettings } from '../../../../../redux/context/customReports/types'
import { CustomReportSectionsType } from '../../../../../redux/context/customReports/typesSection'
import { formatAsDate } from '../../../../../config/dayjsConfig'
import { store } from '../../../../../redux/store'
import { setCompanyChartColorsAction } from '../../../../../redux/context/companySettings/actions'
import { ChartColorPalette } from '../../../../settings/company/general/Appearance/GraphColorSettings'
import { PaperSize, Orientation, PrintScale, MarginSize, computePrintableWidthPx } from './paperConfig'
import usePageBreaks from './usePageBreaks'
import { DEFAULT_PRINT_SETTINGS } from './PrintSettingsContext'
import PrintPaper from './PrintPaper'

interface PrintReportLoaderData {
  report: CustomReport
  sections: (CustomReportSectionsType | null)[]
  companyName?: string
  logoDataUrl?: string
}

export const printReportLoader = async ({ params, request }: LoaderFunctionArgs): Promise<PrintReportLoaderData> => {
  const url = new URL(request.url)
  const token = sessionStorage.getItem('print-token') || ''
  const companyId = url.searchParams.get('companyId') || ''
  const date = url.searchParams.get('date') || undefined
  const language = url.searchParams.get('language') || 'fi'
  const argsParam = url.searchParams.get('args')
  const args = argsParam ? JSON.parse(argsParam) : undefined

  const api = axios.create({
    baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
    headers: token ? { 'X-Token': token } : {}
  })

  if (!i18n.isInitialized) {
    await new Promise<void>(resolve => {
      i18n.on('initialized', resolve)
    })
  }
  await i18n.changeLanguage(language)

  const { data: report } = await api.get<CustomReport>(
    `/companies/${companyId}/reporting/custom/reports/${params.reportId}`
  )

  const sections = report.sections || []
  const sectionQueryParams = {
    lng: language,
    date: formatAsDate(date),
    ...(args?.length > 0 && { args: JSON.stringify(args) })
  }

  // TODO: Switch to batch endpoint when backend v3 is ready — benchmarked ~5x faster.
  // GET /companies/:companyId/reporting/custom/reports/:reportId/sections/:sectionIds([0-9,]+)
  //
  // const sectionIds = sections.map(s => s.id).join(',')
  // const { data } = await api.get<CustomReportSectionsType[]>(
  //   `/companies/${companyId}/reporting/custom/reports/${params.reportId}/sections/${sectionIds}`,
  //   { params: sectionQueryParams }
  // )
  // const sectionResults = sections.map(s => data.find(d => d.id === s.id) ?? null)

  const sectionResults = await Promise.allSettled(
    sections.map(s =>
      api.get<CustomReportSectionsType>(
        `/companies/${companyId}/reporting/custom/reports/${s.reportId}/sections/${s.id}`,
        { params: sectionQueryParams }
      )
    )
  ).then(results => results.map(r => (r.status === 'fulfilled' ? r.value.data : null)))

  let companyName: string | undefined
  let logoDataUrl: string | undefined
  const contractId = url.searchParams.get('contractId')

  try {
    const { data: company } = await api.get(`/companies/${companyId}/group`)
    companyName = company?.name
  } catch {
    // noop
  }

  try {
    const { data: chartColorPalette } = await api.get<ChartColorPalette>(
      `/companies/${companyId}/settings/chart-color-palette`
    )
    if (chartColorPalette?.colors) {
      store.dispatch(setCompanyChartColorsAction(chartColorPalette.colors))
    }
  } catch {
    // noop, falls back to default Highcharts theme colors
  }

  if (contractId) {
    try {
      const logoResponse = await api.get(`/contracts/${contractId}/logo`, { responseType: 'blob' })
      logoDataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = reject
        reader.readAsDataURL(logoResponse.data)
      })
    } catch {
      // noop
    }
  }

  return { report, sections: sectionResults, companyName, logoDataUrl }
}

// Signals Puppeteer that the page failed to load (loader error or render error)
export const PrintReportErrorPage: React.FC = () => {
  const error = useRouteError()
  useEffect(() => {
    const msg = error instanceof Error ? error.message : String(error)
    document.documentElement.dataset.printError = msg || 'load error'
  }, [error])
  return <div>Error</div>
}

interface ErrorBoundaryState {
  hasError: boolean
}

class PrintErrorBoundary extends React.Component<{ children: React.ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error) {
    document.documentElement.dataset.printError = error.message || 'render error'
  }

  render() {
    const { hasError } = this.state
    const { children } = this.props
    if (hasError) return null
    return children
  }
}

const PrintReportPageInner: React.FC = () => {
  const { report, sections: evaluatedSections, companyName, logoDataUrl } = useLoaderData() as PrintReportLoaderData
  const [searchParams] = useSearchParams()

  const settings = useMemo(() => {
    const overrides: Partial<PrintStyleSettings> = {}
    const paper = searchParams.get('paper')
    const orientation = searchParams.get('orientation')
    const scale = searchParams.get('scale')
    const margin = searchParams.get('margin')
    const forcedBreaks = searchParams.get('forcedBreaks')

    if (paper) overrides.paperSize = paper as PaperSize
    if (orientation) overrides.orientation = orientation as Orientation
    if (scale) overrides.scale = Number(scale) as PrintScale
    if (margin) overrides.margin = margin as MarginSize
    if (searchParams.get('pageNumbers')) overrides.pageNumbers = searchParams.get('pageNumbers') === 'true'
    if (forcedBreaks) overrides.forcedBreaks = JSON.parse(forcedBreaks) as number[]
    if (searchParams.get('watermark') !== null) overrides.watermark = searchParams.get('watermark') !== 'false'

    return { ...DEFAULT_PRINT_SETTINGS, ...overrides }
  }, [searchParams])

  const date = searchParams.get('date') || undefined

  const configSections = report.sections || []

  const evaluatedSectionMap = useMemo(
    () => new Map(configSections.map((s, i) => [s.id, evaluatedSections[i]] as const)),
    [configSections, evaluatedSections]
  )

  const contentRef = useRef<HTMLDivElement>(null)

  const forcedBreakSectionIds = useMemo(
    () => new Set(settings.forcedBreaks ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(settings.forcedBreaks)]
  )

  const { ready: pageBreaksReady } = usePageBreaks({
    contentRef,
    enabled: true,
    paperSize: settings.paperSize,
    orientation: settings.orientation,
    scale: settings.scale,
    margin: settings.margin,
    forcedBreakSectionIds
  })

  useEffect(() => {
    if (!pageBreaksReady) return undefined
    document.documentElement.dataset.printReady = 'true'
    return undefined
  }, [pageBreaksReady])

  return (
    <PrintPaper
      mode="print"
      report={report}
      evaluatedSectionMap={evaluatedSectionMap}
      settings={settings}
      contentRef={contentRef}
      companyName={companyName}
      date={date}
      logoUrl={logoDataUrl ?? null}
      printableWidthPx={computePrintableWidthPx(settings)}
    />
  )
}

const PrintReportPage: React.FC = () => (
  <PrintErrorBoundary>
    <PrintReportPageInner />
  </PrintErrorBoundary>
)

export default PrintReportPage
